import { spErrorTags } from "@eventr/common";
import { ColoursObj, MainButton, TextInputField, Tick, Typography, VerticalExpander } from "@eventr/common-storybook";
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import z from "zod";
import { UserAPI } from "data/User";
import { hasChatEligibility } from "feat/chatpool/chat-eligibility";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { NavigationView } from "feat/layout/NavigationView";
import i18n from "i18n/i18next";
import { useOverlayStore } from "state/overlayState/overlay.store";
import { useRouteGuardContext } from "state/routeGuardProvider";

import QRCode from "react-qr-code";
import { useShallow } from "zustand/react/shallow";
import { useDeviceInfo } from "feat/appState/deviceInfo";
import { errorMessageTranslation } from "i18n/translateHelpers";
import { bs, useBoundStore } from "state/bound.store";
import { defaultAddSnack } from "state/overlayState/overlayContext";
import { Utils, copyToClipboard } from "utils";
import { SharingPreference, sectionsForPreference } from "./invite-sections";

const inviteSchema = z.object({
  emails: z.array(
    z.string().email({
      message: "A valid email address is required",
    }),
  ),
  sharingPreference: z.nativeEnum(SharingPreference),
  textToBeParsed: z.string(),
});

type Invite = z.infer<typeof inviteSchema>;

export function EditMembersAddPage() {
  const { goBack } = useGlobalRouter();
  const { orgConfig, wpUser, users, getEventInfoV2, sections, selectedItinerary, selectedItinID } = useBoundStore(
    useShallow((s) => {
      return {
        selectedItinerary: s.selectedItinerary,
        orgConfig: s.orgConfig,
        wpUser: s.wpUser,
        users: s.users,
        getEventInfoV2: s.getEventInfoV2,
        sections: s.sections,
        selectedItinID: s.selectedItinID,
      };
    }),
  );
  const { version } = useDeviceInfo(
    useShallow((s) => {
      return { version: s.version };
    }),
  );
  const { isAllowed } = useRouteGuardContext();
  const [itinSharecode, setItinSharecode] = useState<string>("");
  const [showQRCode, setShowQRCode] = useState<boolean>(true);
  const [sectionsToUpdate, setSectionsToUpdate] = useState<number[]>([]);

  const initialInvite: Invite = {
    emails: [],
    sharingPreference: SharingPreference.all,
    textToBeParsed: "",
  };
  const [invite, setInvite] = useState<Invite>(initialInvite);

  useEffect(() => {
    setShowQRCode(false);
  }, [invite.sharingPreference, sectionsToUpdate]);

  const getShareCode = useCallback(
    async (currentSharingPreference: string | undefined, sectionsToUpdate: number[] | undefined) => {
      const shareCode = await UserAPI.getShareLink(
        selectedItinID,
        wpUser?.id ?? -1,
        false,
        currentSharingPreference as SharingPreference | undefined,
        sectionsToUpdate,
      );

      setItinSharecode(shareCode.identifier);
      return shareCode.identifier;
    },
    [selectedItinID, wpUser?.id],
  );

  const [lastEmailSent, setLastEmailSent] = useState<string | undefined>(undefined);

  enum ErrorMessageKey {
    email = "emails",
  }

  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  /// Catch if bad data.
  const EventTitle = () => {
    try {
      return getEventInfoV2()?.data?.title || "";
    } catch {
      return "";
    }
  };

  const validate = useCallback((): boolean => {
    const newErrorMessages: Record<string, string> = {};

    const result = inviteSchema.safeParse(invite);

    if (!result.success) {
      result.error.issues.forEach((issue) => {
        if (typeof issue.path[0] === "string") {
          newErrorMessages[issue.path[0]] = issue.message;
        }
      });
    }

    setErrorMessages(newErrorMessages);
    return Object.keys(newErrorMessages).length === 0;
  }, [invite]);

  /**
   * Refetches what the team page is drawn from. The server has already added the invitee to the
   * itinerary's members, but the team list is the org's users filtered by those members, so a
   * newly created user is invisible until the users list is fetched again too.
   */
  async function refreshTeam() {
    try {
      await bs().fetchUsers();
      await bs().forceItineraryMapUpdate();
      await bs().refreshCurrentItinerary();
    } catch (error) {
      spErrorTags("Failed to refresh the team after an invite", ["ADD_MEMBER"], error);
    }
  }

  const handleOnClick = async (event: { preventDefault: () => void }) => {
    Utils.hapticFeedback("light");
    event.preventDefault();
    if (showQRCode) {
      await copyToClipboard(itinSharecode, version);
    } else {
      const x = await getShareCode(
        invite.sharingPreference,
        sectionsForPreference(invite.sharingPreference, sections, sectionsToUpdate),
      );
      await copyToClipboard(x, version);
      setShowQRCode(true);
    }
  };

  const linkText = showQRCode
    ? i18n.t("edit-members.adduser.link-text-copy")
    : i18n.t("edit-members.adduser.link-text-make");

  async function sendInvite() {
    useOverlayStore.getState().setLoading(true);

    /// Check if user is allowed to invite
    if (!isAllowed(["allowedInviteUsers"])) {
      /// Not allowed... button should have been invisiable...
      spErrorTags("Add user button should not be able to be pressed if not allowed to invite users.", ["ADD_MEMBER"]);
      useOverlayStore.getState().setLoading(false);
      return;
    }

    ///Check if user already in WP.
    const invitedEmails = invite.emails.map((email) => email.toLowerCase());
    const checkUser = users.find((user) => {
      return invitedEmails.includes(user.email?.toLowerCase());
    });

    const sectionsToInclude = sectionsForPreference(invite.sharingPreference, sections, sectionsToUpdate);

    /// Deal with user already in wp. Just add to itinerary, which emails them the itinerary link.
    if (checkUser && checkUser.id) {
      const userInItinerary = selectedItinerary?.v2?.[0]?.data?.members?.find((member: { option: number }) => {
        return member.option === checkUser.id;
      });
      if (userInItinerary) {
        /// User already in itinerary
        defaultAddSnack(i18n.t("edit-members.adduser.already-added"), "error");
        useOverlayStore.getState().setLoading(false);
        return;
      }

      try {
        const res = await UserAPI.addUserToItinerary({
          itineraryId: selectedItinID,
          newUserId: checkUser.id,
          currentUserId: wpUser?.id ?? -1,
          sections: sectionsToInclude,
        });
        if (res.status === "success") {
          setErrorMessages({});
          setLastEmailSent(checkUser.email);
          setInvite(initialInvite);
          await refreshTeam();
          defaultAddSnack(i18n.t("edit-members.adduser.added"), "success");
        } else {
          setErrorMessages({ [ErrorMessageKey.email]: res.message || i18n.t("edit-members.adduser.check") });
          defaultAddSnack(i18n.t("edit-members.adduser.error-adding"), "error");
        }
      } catch (error) {
        spErrorTags(i18n.t("edit-members.adduser.error-adding"), ["ADD_MEMBER"], error);
        defaultAddSnack(i18n.t("edit-members.adduser.error-adding"), "error");
      } finally {
        useOverlayStore.getState().setLoading(false);
      }
      return;
    }

    /// Not in the users list. The server also adds and emails an account it already has, which
    /// the list can miss: it leaves out administrators and deleted users.
    const eventTitle = EventTitle();

    try {
      const res = await UserAPI.sendInviteNew({
        eventTitle: eventTitle,
        users: invitedEmails.map((email) => {
          return { email };
        }),
        sections: sectionsToInclude,
        itineraryId: selectedItinID,
        userID: wpUser?.id,
      });
      if (res[0].status === "error") {
        setErrorMessages({ [ErrorMessageKey.email]: res[0].message || i18n.t("edit-members.adduser.check") });

        if (res[0].message === "Email already exists") {
          defaultAddSnack(i18n.t("edit-members.adduser.already-added"), "error");
        } else {
          defaultAddSnack(i18n.t("edit-members.adduser.error-inviting"), "error");
        }
      }
      if (res[0].status === "success") {
        setLastEmailSent(invite.emails[0]);
        setInvite(initialInvite);

        // Servers that send chatEligibility turn chat on when the invite is accepted.
        if (!hasChatEligibility(orgConfig)) {
          bs().setOrgConfig({
            ...orgConfig,
            isRocketChatInitialised: true,
          });
        }
        setErrorMessages({});
        await refreshTeam();
        defaultAddSnack(i18n.t("edit-members.adduser.invitesSent"), "success");
      }
    } catch (error) {
      spErrorTags("Error inviting user", ["ADD_MEMBER"], error);
      defaultAddSnack(i18n.t("edit-members.adduser.error-inviting"), "error");
    } finally {
      useOverlayStore.getState().setLoading(false);
    }
  }

  const sharingPreferencePicker = (
    <>
      <Stack sx={{
        gap: "16px"
      }}>
        <Box>
          <Typography color={ColoursObj.core.primary}>
            {i18n.t("edit-members.adduser.will-add-to", {
              eventName: EventTitle(),
              interpolation: { escapeValue: false },
            })}
          </Typography>
        </Box>

        <RadioGroup
          value={invite.sharingPreference}
          onChange={(event) => {
            setInvite({
              ...invite,
              sharingPreference: (event.target as HTMLInputElement).value as SharingPreference,
            });
          }}
        >
          <FormControlLabel
            id="travelwithMeOption"
            value={SharingPreference.all}
            control={<Radio />}
            label={
              <Stack>
                <Typography color={ColoursObj.core.primary}>
                  {i18n.t("edit-members.adduser.travelling-with-me")}
                </Typography>
                <Typography variant="bodySubtext" color={ColoursObj.core.primary}>
                  {i18n.t("edit-members.adduser.itinerary-matches")}
                </Typography>
              </Stack>
            }
            sx={{
              paddingBottom: "16px",
            }}
          />
          <FormControlLabel
            id="shareSomeitineraryOption"
            value={SharingPreference.some}
            control={<Radio />}
            label={
              <Stack>
                <Typography color={ColoursObj.core.primary}>
                  {i18n.t("edit-members.adduser.itinerary-shares-some")}
                </Typography>
                <Typography variant="bodySubtext" color={ColoursObj.core.primary}>
                  {i18n.t("edit-members.adduser.itinerary-matches-some")}
                </Typography>
              </Stack>
            }
            sx={{
              paddingBottom: "16px",
            }}
          />

          <VerticalExpander open={invite.sharingPreference === SharingPreference.some}>
            <FormGroup
              onChange={(event) => {
                const key = (event.target as HTMLInputElement).value;
                if (sectionsToUpdate.includes(Number(key))) {
                  setSectionsToUpdate(
                    sectionsToUpdate.filter((element) => {
                      return element !== Number(key);
                    }),
                  );
                } else {
                  setSectionsToUpdate(sectionsToUpdate.concat([Number(key)]));
                }
              }}
              sx={{
                paddingLeft: "32px",
                paddingBottom: "16px",
              }}
            >
              {sections.slice(1).map((sectionKey, index) => {
                return (
                  <FormControlLabel
                    color={ColoursObj.core.primary}
                    id={sectionKey.name}
                    key={index}
                    label={
                      <Typography color={ColoursObj.core.primary}>
                        {i18n.t(`sections.${sectionKey.name.toLocaleLowerCase().replace(" ", "_")}`, { count: 0 })}
                      </Typography>
                    }
                    control={<Checkbox checked={sectionsToUpdate.includes(sectionKey.id)} value={sectionKey.id} />}
                  />
                );
              })}
            </FormGroup>
          </VerticalExpander>

          <FormControlLabel
            id="ownPlansOption"
            value={SharingPreference.none}
            control={<Radio />}
            label={
              <Stack>
                <Typography color={ColoursObj.core.primary}>{i18n.t("edit-members.adduser.own-plans")}</Typography>
                <Typography variant="bodySubtext" color={ColoursObj.core.primary}>
                  {i18n.t("edit-members.adduser.itinerary-doesnt-match")}
                </Typography>
              </Stack>
            }
          />
        </RadioGroup>
      </Stack>
    </>
  );

  const sendButton = (
    <Button
      id="sendInviteButton"
      disabled={
        invite.textToBeParsed === "" ||
        invite.emails.length === 0 ||
        (invite.sharingPreference === "some" && sectionsToUpdate.length === 0)
      }
      onClick={() => {
        if (validate()) {
          sendInvite();
        }
      }}
    >
      {i18n.t("edit-members.adduser.send-invite")}
    </Button>
  );

  const confirmationDialog = (
    <>
      {lastEmailSent !== undefined && (
        <Stack
          sx={{
            padding: "16px",
            marginBottom: "16px",
            gap: "8px",
            bgcolor: ColoursObj.shades.shade4,
            borderRadius: "16px",
            backgroundColor: ColoursObj.backgrounds.secondary
          }}>
          <Stack direction="row" sx={{
            gap: "8px"
          }}>
            <Tick fill={ColoursObj.states.success} />
            <Typography
              variant="bodyBold"
              id={"Invite sent to " + lastEmailSent.toLowerCase()}
              sx={{
                maxWidth: "Calc(100% - 32px)",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
              {i18n.t("edit-members.adduser.invite-sent", {
                email: lastEmailSent.toLowerCase(),
              })}
            </Typography>
          </Stack>

          <Typography variant="bodySubtext" id="inviteWillReceiveText">
            {i18n.t("edit-members.adduser.invite-sent-text")}
          </Typography>
        </Stack>
      )}
    </>
  );

  return (
    <>
      <NavigationView
        usePageTransition
        navigationBarTitle={i18n.t("edit-members.adduser.add-team-member")}
        navigationBarSubtitle={EventTitle()}
        backButtonAction={() => {
          goBack();
        }}
      >
        <Stack
          sx={{
            padding: "16px",
            height: "100%",
            alignContent: "space-between",
            paddingBottom: "30%",
            overflow: "scroll",
            gap: "24px"
          }}>
          <Stack>{sharingPreferencePicker}</Stack>
          <TextInputField
            id="emailInput"
            required
            fieldLabel={i18n.t("general.email")}
            error={Object.keys(errorMessages).includes(ErrorMessageKey.email)}
            style={{ color: "red" }}
            placeholder={i18n.t("edit-members.adduser.enter-email-to-invite")}
            value={invite.textToBeParsed}
            onChange={(event) => {
              /// Parsing emails...
              let emailList = invite.emails;
              try {
                const tmpEmailList = event.currentTarget.value.replaceAll(" ", "");
                emailList = tmpEmailList.split(",");
              } catch (error) {
                /// could not parse.
                spErrorTags(error, ["ERROR", "EmailInvites"]);
              }

              setInvite({
                ...invite,
                textToBeParsed: event.currentTarget.value,
                emails: emailList,
              });
            }}
            type="email"
            errorMessage={
              Object.keys(errorMessages).includes(ErrorMessageKey.email)
                ? errorMessageTranslation(errorMessages[ErrorMessageKey.email] ?? "")
                : ""
            }
          />
          <Stack>
            {confirmationDialog}
            {sendButton}
          </Stack>
          <Stack
            sx={{
              justifyContent: "center",
              flexDirection: "row",
              alignItems: "center",
              gap: "16px"
            }}>
            <Typography color={ColoursObj.core.primary}>{i18n.t("general.or")}</Typography>
          </Stack>

          <Stack
            sx={{
              gap: "16px",
              backgroundColor: ColoursObj.backgrounds.secondary,
              borderRadius: "8px",
              padding: "16px",
              color: ColoursObj.core.primary
            }}>
            <Stack
              sx={{
                justifyContent: "space-between",
                flexDirection: "row",
                alignItems: "center",
                gap: "16px"
              }}>
              <Typography id="shareLinkText" variant="bodyRegular">
                {linkText}
              </Typography>
            </Stack>

            <MainButton
              id="shareLinkButton"
              text={i18n.t("edit-members.adduser.copyShareLink")}
              onClick={handleOnClick}
            />

            {showQRCode && (
              <Stack>
                <div
                  style={{
                    height: "auto",
                    margin: "0 auto",
                    maxWidth: 256,
                    width: "100%",
                  }}
                >
                  <QRCode
                    id="shareLinkQRCode"
                    size={256}
                    style={{
                      height: "auto",
                      maxWidth: "100%",
                      width: "100%",
                    }}
                    level="Q"
                    value={itinSharecode}
                    viewBox={`0 0 256 256`}
                  />
                </div>
              </Stack>
            )}
          </Stack>
        </Stack>
      </NavigationView>
    </>
  );
}
